import React, { useEffect, useState } from 'react'
import ReactPaginate from 'react-paginate'
import { Helmet } from 'react-helmet'
import ResponsiveAppBar from '../common/ResponsiveAppBar'
import { Contacts as ContactsIcon } from '@mui/icons-material'
import {
  TableCell,
  Table,
  TableRow,
  TableBody,
  TableHead,
  Paper,
  Button,
  Grid,
  DialogContent,
  DialogActions,
  TextField,
  Dialog,
  Typography,
  FormControl,
  Tooltip,
  CircularProgress,
} from '@mui/material'
import { AddCircle } from '@mui/icons-material'
import { IReferralSpecialitySummary } from 'src/viewModels/referrals/ReferralSpecialitySummary'
import {
  getReferralSourceSpecialties,
  addReferralSourceSpeciality,
} from 'src/services/AdminService'
import { pagedList } from 'src/viewModels/pagedList'
import moment from 'moment'

const ReferralSpeciality: React.FC = () => {
  const [gridData, setGridData] = useState<IReferralSpecialitySummary[]>(
    new pagedList().items
  )
  const [gridLoading, setGridLoading] = useState(false)
  const [openModal, setOpenModal] = useState(false)
  const [newSpeciality, setNewSpeciality] = useState('')

  const [totalItems, setTotalItems] = useState(0)
  const [firstRecordIndex, setFirstRecordIndex] = useState(0)
  const [lastRecordIndex, setLastRecordIndex] = useState(0)
  const [searchSpeciality, setSearchSpeciality] = useState('')

  const [pageCount, setPageCount] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize] = useState(25)

  const handleAddClick = () => {
    setOpenModal(true)
  }

  const handleCloseModal = () => {
    setOpenModal(false)
    setNewSpeciality('')
  }

  const handleSave = async () => {
    setGridLoading(true)
    try {
      await addReferralSourceSpeciality(newSpeciality)
      handleCloseModal()
      await loadSpecialties()
    } finally {
      setGridLoading(false)
    }
  }

  const setPaginationOffsetData = (totalItems: number) => {
    const firstIndex = (page - 1) * pageSize + 1
    const lastIndex = page * pageSize < totalItems ? page * pageSize : totalItems
    setFirstRecordIndex(firstIndex)
    setLastRecordIndex(lastIndex)
  }

  const handlePageChange = (event: any) => {
    const newPage = event.selected + 1
    setPage(newPage)
    getReferralSourceSpecialties(searchSpeciality.trim() || null, newPage, pageSize)
  }

  const paginationInfo = () => {
    if (!isSearchGridDataEmpty()) {
      return (
        <div className="pagination">
          <Typography variant="caption" gutterBottom>
            Showing {firstRecordIndex} to {lastRecordIndex} of {totalItems} entries
          </Typography>
        </div>
      )
    }
    return <></>
  }

  const loadSpecialties = async () => {
    setGridLoading(true)

    const response = await getReferralSourceSpecialties(
      searchSpeciality.trim() || null,
      page,
      pageSize
    )

    const { items, totalItems, totalPages } = response

    setTotalItems(totalItems)
    setPageCount(totalPages)
    setGridData(items)

    if (totalItems > 0) {
      setPaginationOffsetData(totalItems)
    }
    setGridLoading(false)
  }

  const isSearchGridDataEmpty = () => {
    return !gridData || gridData.length === 0
  }

  const clearSearch = async () => {
    setSearchSpeciality('')
    setPage(1)
    setTotalItems(0)
    setPageCount(0)
    setGridData([])
    setGridLoading(false)
  }

  const search = async (event: any) => {
    event.preventDefault()
    setGridLoading(true)
    setPage(1)
    try {
      const response = await getReferralSourceSpecialties(
        searchSpeciality.trim() || null,
        page,
        pageSize
      )

      const { items, totalItems, totalPages } = response

      setTotalItems(totalItems)
      setPageCount(totalPages)
      setGridData(items)

      if (totalItems > 0) {
        setPaginationOffsetData(totalItems)
      }
    } finally {
      setGridLoading(false)
    }
  }

  const mapSearchGridData = () => {
    if (isSearchGridDataEmpty()) {
      return (
        <TableRow className="gridNoPadding" key={1}>
          <TableCell colSpan={3} align="center">
            No records found
          </TableCell>
        </TableRow>
      )
    }

    return gridData.map((item, index) => (
      <TableRow className="gridPadding" key={index}>
        <TableCell>{item.speciality}</TableCell>
        <TableCell style={{ textAlign: 'right', paddingRight: '160px' }}>
          {item.createdDate ? moment.utc(item.createdDate).format('MM/DD/YYYY') : ''}
        </TableCell>
        <TableCell>{item.createdByUser}</TableCell>
      </TableRow>
    ))
  }

  useEffect(() => {
    loadSpecialties()
  }, [page, pageSize])

  return (
    <>
      <Helmet>
        <title>Referral Speciality</title>
      </Helmet>
      <ResponsiveAppBar title="Referral Speciality" pageIcon={<ContactsIcon />} />
      <Paper
        style={{ width: '50%', margin: '20px', height: '100%', paddingBottom: '20px' }}
      >
        {gridLoading && (
          <div
            style={{
              position: 'fixed',
              display: 'flex',
              width: '100%',
              height: '100%',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              cursor: 'pointer',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: '2',
            }}
          >
            <CircularProgress size={100} />
          </div>
        )}
        <form onSubmit={search}>
          <Grid container style={{ padding: '10px', width: '100%' }} xs={12}>
            <Typography
              color="primary"
              component="h6"
              variant="h6"
              gutterBottom
              style={{ paddingBottom: '10px' }}
            >
              SEARCH CRITERIA
            </Typography>
            <Grid container direction="row" spacing={2} style={{ width: '100%' }}>
              <Grid item xs={3}>
                <FormControl fullWidth>
                  <TextField
                    value={searchSpeciality}
                    onChange={(e) => setSearchSpeciality(e.target.value)}
                    label="Referral Speciality"
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ fullWidth: true }}
                    variant="outlined"
                  />
                </FormControl>
              </Grid>
              <Grid item container xs={5} spacing={2}></Grid>
              <Grid item xs={4} container justifyContent="flex-end">
                <Tooltip title="Add Referral Speciality">
                  <AddCircle
                    color="primary"
                    style={{ fontSize: '30px', cursor: 'pointer' }}
                    onClick={handleAddClick}
                  />
                </Tooltip>
              </Grid>
            </Grid>
            <Grid
              container
              direction="row"
              spacing={2}
              style={{
                marginLeft: '1px',
                marginTop: '10px',
                paddingTop: '5px',
                paddingBottom: '10px',
                width: '100%',
              }}
            >
              <Button
                type="submit"
                color="primary"
                size="medium"
                variant="contained"
                style={{ marginRight: '10px' }}
              >
                Search
              </Button>
              <Button
                color="secondary"
                onClick={clearSearch}
                size="medium"
                variant="contained"
              >
                Clear Search
              </Button>
            </Grid>
          </Grid>
        </form>
        <Grid
          item
          container
          direction="row"
          justifyContent="space-between"
          alignContent="center"
          alignItems="flex-start"
          wrap="wrap"
        >
          <Grid item xs={12}>
            <Table aria-label="simple table" style={{ padding: '20px' }}>
              <TableHead>
                <TableRow className="gridPadding">
                  <TableCell style={{ width: '33.33%' }}>Speciality</TableCell>
                  <TableCell
                    style={{ width: '33.33%', textAlign: 'right', paddingRight: '160px' }}
                  >
                    Date Created
                  </TableCell>
                  <TableCell style={{ width: '33.33%' }}>Created By</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>{mapSearchGridData()}</TableBody>
            </Table>
            <div className="pagination-row">
              {paginationInfo()}
              <ReactPaginate
                previousLabel={'<'}
                nextLabel={'>'}
                onPageChange={handlePageChange}
                forcePage={page - 1}
                pageCount={pageCount}
                containerClassName={'pagination'}
                activeClassName={'active'}
                renderOnZeroPageCount={() => null}
              />
            </div>
          </Grid>
        </Grid>
      </Paper>
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        disableEscapeKeyDown
        maxWidth="xs"
        fullWidth
      >
        <ResponsiveAppBar
          title="Add Referral Speciality"
          pageIcon={<ContactsIcon />}
          isModal={true}
        />
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Referral Speciality"
            type="text"
            fullWidth
            value={newSpeciality}
            onChange={(e) => setNewSpeciality(e.target.value)}
            InputLabelProps={{ shrink: true, required: true }}
            InputProps={{ fullWidth: true }}
            inputProps={{ maxLength: 25 }}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            disabled={!newSpeciality.trim() || gridLoading}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default ReferralSpeciality
